
import { mapGetters } from "vuex";
import { metaGenerator } from "@/utils";
import googleAnalyticsEvents from "~/mixins/googleAnalyticsEvents.js";
import QuizPagination from "~/components/journey-quiz/QuizPagination.vue";
import UiButton from "~/components/UI/UiButton.vue";
import ArrowRightSvg from "~/components/svg/ArrowRightSvg.vue";
import useEvents from "~/mixins/useEvents";

export default {
  name: "Journey",
  components: { ArrowRightSvg, UiButton, QuizPagination },
  mixins: [googleAnalyticsEvents, useEvents],
  async asyncData({ $api, error, i18n }) {
    const METAL_GROUPS = [
      {
        title: i18n.t("gold"),
        value: "gold",
        skus: [
          "10KY",
          "10KW",
          "10KR",
          "14KY",
          "14KW",
          "14KR",
          "18KY",
          "18KW",
          "18KR",
          "10K_ZLUTE_ZLATO",
          "10K_BILE_ZLATO",
          "10K_RUZOVE_ZLATO",
          "14K_ZLUTE_ZLATO",
          "14K_BILE_ZLATO",
          "14K_RUZOVE_ZLATO",
          "18K_ZLUTE_ZLATO",
          "18K_BILE_ZLATO",
          "18K_RUZOVE_ZLATO",
          "10K_ZOLTE_ZLOTO",
          "10K_BIALE_ZLOTO",
          "10K_ROZOWE_ZLOTO",
          "14K_ZOLTE_ZLOTO",
          "14K_BIALE_ZLOTO",
          "14K_ROZOWE_ZLOTO",
          "18K_ZOLTE_ZLOTO",
          "18K_BIALE_ZLOTO",
          "18K_ROZOWE_ZLOTO",
        ],
        image_url: "/images/gold.png",
      },
      // {
      //   title: i18n.t("platinum"),
      //   value: "platinum",
      //   skus: ["PT", "PLATINUM"],
      //   image_url: "/images/white_gold.png",
      // },
      {
        title: i18n.t("silver"),
        value: "silver",
        skus: ["SS", "BILE_STRIBRO", "BIALE_SREBRO"],
        image_url: "/images/silver.png",
      },
      {
        title: i18n.t("goldOverSilver"),
        value: "gold-over-silver",
        skus: [
          "GOS",
          "ROS",
          "ZLUTE_STRIBRO",
          "RUZOVE-STRIBRO",
          "ROZOWE_SREBRO",
          "ZOLTE_SREBRO",
        ],
        image_url: "/images/ros_gold.png",
      },
    ];

    const getPage = async () => {
      try {
        const { data } = await $api.pages.getPage("journey");
        return data.data;
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
        error({ statusCode: err?.response?.status });
      }
    };

    const getEntities = async (type, ids) => {
      try {
        const { data } = await $api.products.getEntities(type || "", { ids });
        return data;
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors);
        error({ statusCode: err?.response?.status });
      }
    };

    const res = await getPage();
    const blocks = res?.blocks;
    const translation = res?.mixedTranslation;
    const medias = res?.medias;

    const entityPromises = [];
    const entityBlocksData = {};

    const quizResult = [];

    blocks.forEach((block, idx) => {
      if (block.type.identifier === "entity") {
        entityPromises.push(
          getEntities(
            block.mixedTranslation.content.type,
            block.mixedTranslation.content.entities.reduce((acc, el) => {
              if (el.active) {
                acc.push(el.id);
              }
              return acc;
            }, [])
          )
        );
        entityBlocksData[idx] = [];
        if (block.name !== "journey-diamond-hc") {
          quizResult.push({
            type: block.mixedTranslation.content.type,
            value: null,
          });
        }
      } else {
        quizResult.push({
          type: block.mixedTranslation.content.buttonUrl,
          value: null,
        });
      }
    });

    const promisesRes = await Promise.all(entityPromises);
    let metalsData = [];
    let diamondsData = [];
    Object.keys(entityBlocksData).forEach((el, idx) => {
      if (blocks[el].name.includes("metals")) {
        metalsData = promisesRes[idx].data;
        entityBlocksData[el] = METAL_GROUPS.map((el) => ({
          mixedTranslation: { title: el.title },
          sku: el.value,
          medias: [{ file: { url: el.image_url }, position: "list" }],
        }));
      } else if (blocks[el].name === "journey-diamond-hc") {
        diamondsData = promisesRes[idx].data;
      } else {
        entityBlocksData[el] =
          blocks?.[idx]?.mixedTranslation?.content?.entities?.reduce(
            (acc, el) => {
              const item = promisesRes[idx].data.find((i) => i.id === el.id);
              if (item) {
                acc.push(item);
              }
              return acc;
            },
            []
          ) || [];
      }
    });

    return {
      components: blocks.filter((el) => el.name !== "journey-diamond-hc") || [],
      pageContent: { ...translation, medias },
      activeStep: 1,
      entityBlocksData,
      quizResult,
      isMounted: false,
      METAL_GROUPS,
      metalsData,
      diamondsData,
    };
  },
  head() {
    const i18nHead = this.$nuxtI18nHead({
      addSeoAttributes: true,
    });
    const titleTemplate = `%s | ${this.getCompanyName}`;
    return {
      meta: [
        ...metaGenerator({
          title: this.pageContent?.meta_title || this.pageContent?.title || "",
          description: this.pageContent?.meta_description || "",
          image:
            this.pageContent?.medias?.find((el) => el.position === "open-graph")
              ?.file?.url || "",
          keywords:
            this.pageContent?.meta_keywords || this.pageContent?.title || "",
          url:
            this.pageContent?.meta_url ||
            process.env.DOMAIN + this.$route.fullPath,
          company_name: this.getCompanyName,
          titleTemplate,
        }),
        ...i18nHead.meta,
      ],
      htmlAttrs: i18nHead.htmlAttrs,
      link: i18nHead.link,
      title: this.pageContent?.meta_title || this.pageContent?.title || "",
      titleTemplate,
    };
  },
  computed: {
    ...mapGetters({
      getCompanyName: "general/getCompanyName",
    }),
    canSendGoogleAnalyticsStatistic() {
      return this.integrationCallbackStatuses.TYPE_GOOGLE_ANALYTICS;
    },
    activeComponent() {
      return this.components[this.activeStep - 1];
    },
    activeComponentName() {
      if (this.activeComponent.name.includes("journey-list")) {
        return "quiz-list";
      } else if (this.activeComponent.name.includes("journey-btns")) {
        return "quiz-btns";
      } else {
        return this.activeComponent.type.identifier;
      }
    },
  },
  watch: {
    canSendGoogleAnalyticsStatistic: {
      handler() {
        this.gaDynamicParamsEvent("View Other", {
          ecomm_prodid: "",
          ecomm_pagetype: "other",
          ecomm_totalvalue: "",
          ecomm_category: "",
        });
      },
      immediate: true,
    },
    "$route.query": {
      handler(val) {
        this.quizResult.forEach((el, idx) => {
          if (el.type === "materials") {
            const currentMaterials = Array.isArray(this.$route.query.materials)
              ? this.$route.query.materials
              : this.$route.query.materials
              ? [this.$route.query.materials]
              : [];

            currentMaterials.forEach((material) => {
              if (
                this.entityBlocksData[idx] &&
                this.entityBlocksData[idx].find((i) => i.sku === material)
              ) {
                el.value = material;
              } else if (
                this.components?.[idx]?.mixedTranslation?.content?.slides?.find(
                  (el) => material === el.buttonUrl
                )
              ) {
                el.value = material;
              }
            });
          } else if (val[el.type]) {
            el.value = val[el.type];
          }
        });

        if (!this.isMounted) {
          const emptyStepIdx = this.quizResult.findIndex((el) => !el.value);
          this.activeStep =
            emptyStepIdx < 0 ? this.components.length : emptyStepIdx + 1;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.visitEvent();
    this.isMounted = true;
  },
  methods: {
    async onSelect(data) {
      if (data.type === "collections") {
        await this.$router.replace({
          query: { ...this.$route.query, collections: data.item.slug },
        });
      } else if (data.type === "categories") {
        await this.$router.replace({
          query: { ...this.$route.query, categories: data.item.sku },
        });
      } else if (data.type === "price") {
        await this.$router.replace({
          query: { ...this.$route.query, price: data.item.buttonUrl },
        });
      } else if (data.type === "materials") {
        const currentStepValue = this.quizResult[this.activeStep - 1].value;
        const currentMaterials = Array.isArray(this.$route.query.materials)
          ? this.$route.query.materials
          : this.$route.query.materials
          ? [this.$route.query.materials]
          : [];
        let filteredMaterials = data.item.buttonUrl || data.item.sku;

        if (currentStepValue && currentMaterials.includes(currentStepValue)) {
          filteredMaterials = currentMaterials.filter(
            (el) => el !== currentStepValue
          );

          filteredMaterials.push(data.item.buttonUrl || data.item.sku);
        } else {
          filteredMaterials = [...currentMaterials, filteredMaterials];
        }
        await this.$router.replace({
          query: { ...this.$route.query, materials: filteredMaterials },
        });
      }

      this.nextStep();
    },
    nextStep() {
      if (this.activeStep < this.components?.length) {
        this.activeStep += 1;
      } else {
        const collection = this.quizResult.find(
          (el) => el.type === "collections"
        );
        const query = this.quizResult.reduce((acc, el) => {
          if (el.type === "collections") {
            return acc;
          } else if (el.type === "materials") {
            if (!acc[el.type]) {
              acc[el.type] = [];
            }

            const collectionIdx = this.quizResult.findIndex(
              (el) => el.type === "collections"
            );

            const currentCollection = this.entityBlocksData?.[
              collectionIdx
            ]?.find((colect) => colect.slug === collection?.value);

            const replacedMetal = this.METAL_GROUPS.find(
              (metalGroup) => metalGroup.value === el.value
            );
            const replacedDiamond = this.diamondsData.find(
              (diamond) => diamond.sku === el.value
            );

            if (replacedMetal) {
              replacedMetal.skus.forEach((sku) => {
                const currentMetal = this.metalsData.find(
                  (el) => el.sku === sku
                );
                if (
                  currentMetal?.materialCollections?.find(
                    (col) => col.collection_id === currentCollection.id
                  )
                ) {
                  acc[el.type].push(sku);
                }
              });
            } else if (replacedDiamond) {
              if (
                replacedDiamond?.materialCollections.find(
                  (col) => col.collection_id === currentCollection.id
                )
              ) {
                acc[el.type].push(replacedDiamond.sku);
              }
            } else {
              acc[el.type] = [...acc[el.type], el.value];
            }
            return acc;
          } else if (el.type === "price") {
            acc.min_cost_from = el.value.split("|")[0];
            acc.max_cost_to = el.value.split("|")[1];
          } else {
            acc[el.type] = el.value;
          }
          return acc;
        }, {});
        // console.log("query - ", query);
        if (collection) {
          this.$router.push(
            this.localePath({
              path: `/collection/${collection.value}`,
              query: { ...query },
            })
          );
        } else {
          this.$router.push(
            this.localePath({
              path: `/catalog`,
              query: { ...query },
            })
          );
        }
      }
    },
  },
};
